import { EmptyData } from '@/components/lib/EmptyData'
import { Loading } from '@/components/lib/Loading'
import CompanyMenu from '@/components/Menu/CompanyMenu'
import useMediaQuery from '@/hooks/use-media-query'
import { useUserRole } from '@/hooks/useUserRole'
import AuthService from '@/services/Auth/AuthService'
import CompanyService from '@/services/Company/CompanyService'
import { CompanyType } from '@/types/CompanyType'
import { setDateTime } from '@/utils/Functions'
import { flashStore } from '@/utils/Store'
import * as Sentry from '@sentry/react'
import { AxiosError, AxiosResponse } from 'axios'
import { Plus } from 'lucide-react'
import React from 'react'
import { Button, Card, Col, Container, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal, { SweetAlertResult } from 'sweetalert2'

interface ErrorResponse {
  response: AxiosResponse
  hydra: {
    description: string
  }
}


const MD_BREAKPOINT = '(min-width: 769px)'

export const CompanyListTable = () => {
  const isDesktop = useMediaQuery(MD_BREAKPOINT)

  /* States / Hooks
   *******************************************************************************************/
  const [companies, setCompanies] = React.useState<CompanyType[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [nextLoading, setNextLoading] = React.useState<boolean>(false)
  const [nextPage, setNextPage] = React.useState<string | undefined>()
  const [currentPage] = React.useState<number>(1)
  const [perPage] = React.useState<number>(20)
  const flash = flashStore()
  const myRole = useUserRole()

  /* useEffect
   *******************************************************************************************/
  React.useEffect(() => {
    getData(currentPage, perPage)
  }, [currentPage, perPage])

  /* Functions
   *******************************************************************************************/
  const getData = (page: number, perPage: number) => {
    CompanyService.getCompanyPerPage(page, perPage)
      .then((response: AxiosResponse) => {
        setCompanies(response.data['hydra:member'])
        setNextPage(response.data['hydra:view']['hydra:next'])
      })
      .catch((error: AxiosError) => {
        AuthService.refreshToken()
        Sentry.captureException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleNextPage = () => {
    setNextLoading(true)
    CompanyService.getCompanyLoadNextPage(nextPage)
      .then((response: AxiosResponse) => {
        setCompanies(companies.concat(response.data['hydra:member']))
        setNextPage(response.data['hydra:view']['hydra:next'])
      })
      .catch((error: AxiosError) => {
        AuthService.refreshToken()
        Sentry.captureException(error)
      })
      .finally(() => setNextLoading(false))
  }

  const handleDelete = (data: CompanyType) => {
    Swal.fire({
      title: 'Confirmation !',
      text: 'Voulez-vous vraiment supprimer la société ' + data.name + ' ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, je confirme',
      cancelButtonText: 'Non',
    }).then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        CompanyService._delete(Number(data.id))
          .then(() => {
            flash.setMessage('success', 'Société ' + data.name + ' supprimée !')
            getData(currentPage, perPage)
          })
          .catch((e: ErrorResponse) => {
            if (e?.response?.status == 403) {
              flash.setMessage('error', e?.response?.data['hydra:description'])
            }
          })
      }
    })
  }

  const companyListMobile = () => (
    <Container fluid className='px-0'>
      {companies.map((company) => {
        const companyMenuProps = { company, handleDelete }
        return (
          <Card key={company.id} className='border-green shadow px-0 py-3 mb-4 w-100'>
            <div className='d-flex align-items-center gap-0 px-2'>
              <Col xs={1} className='ps-2'></Col>
              <Col xs={10} className='d-flex flex-column justify-content-center'>
                <Link to={'/societes/detail/' + company?.id}>
                  <div className='d-flex flex-column justify-content-center align-items-center'>
                    <span className='mb-2'>{company?.name}</span>
                    <span className='text-center font-75'>{company.contactEmail}</span>
                    <span className='text-center font-75 '>{company.contactPhone}</span>
                    <span className='text-center font-75 '>
                      {setDateTime(company?.createdAt)}
                    </span>
                  </div>
                </Link>
              </Col>
              <Col xs={1} className=''>
                <CompanyMenu companyMenuProps={companyMenuProps} />
              </Col>
            </div>
          </Card>
        )
      })}
    </Container>
  )

  const companyTableDesktop = () => (
    <Table striped hover responsive>
      <thead>
        <tr>
          <th>#</th>
          <th>Nom</th>
          <th>Client / Organisation</th>
          <th>Adresse email</th>
          <th>Téléphone</th>
          <th>Date de création</th>
          {myRole === import.meta.env.VITE_APP_ROLE_ADMIN ||
            (myRole === import.meta.env.VITE_APP_ROLE_SUPER_ADMIN && <th>Actions</th>)}
        </tr>
      </thead>
      <tbody>
        {companies.map((company: CompanyType) => {
          const companyMenuProps = { company, handleDelete }
          return(
          <tr key={company?.id}>
            <td>
              <Link to={'/societes/detail/' + company?.id}>{company?.id}</Link>
            </td>
            <td>
              <Link to={'/societes/detail/' + company?.id}>{company?.name}</Link>
            </td>
            <td>
              <Link to={'/societes/detail/' + company?.id}>{company?.contactName}</Link>
            </td>
            <td>
              <Link to={'/societes/detail/' + company?.id}>{company?.contactEmail}</Link>
            </td>
            <td>
              <Link to={'/societes/detail/' + company?.id}>{company?.contactPhone}</Link>
            </td>
            <td>
              <Link to={'/societes/detail/' + company?.id}>
                {setDateTime(company?.createdAt)}
              </Link>
            </td>
            {myRole === import.meta.env.VITE_APP_ROLE_ADMIN ||
              (myRole === import.meta.env.VITE_APP_ROLE_SUPER_ADMIN && (
                <td>
                  <CompanyMenu companyMenuProps={companyMenuProps} />
                </td>
              ))}
          </tr>
        )})}
      </tbody>
    </Table>
  )

  /* Render
   *******************************************************************************************/
  return (
    <>
      {loading ? (
        <Loading />
      ) : companies?.length === 0 ? (
        <EmptyData />
      ) : (
        <>
          {isDesktop ? companyTableDesktop() : companyListMobile()}

          {nextPage !== undefined && (
            <div className='ms-auto mb-3 me-3'>
              <Button variant='dark' className='d-inline w-20' onClick={handleNextPage}>
                {nextLoading ? (
                  'En cours de chargement...'
                ) : (
                  <>
                    <Plus size={20} /> Charger plus{' '}
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}
